// Central content + brand config for Thoratech static site.
// Edit contact details here to update across the site.

export const BRAND = {
  name: "Thoratech",
  legal: "Thoratech Innovations Pvt Ltd",
  tagline: "From process to progress.",
  city: "Pune, India",
  yearIncorporated: 2024,
  contact: {
    email: "hello@thoratech.global",
    phone: "+91 84858 34674",
    phoneDirect: "+91 99234 01144",
    linkedin: "https://www.linkedin.com/company/thoratech-global/",
    linkedinFounder: "https://www.linkedin.com/in/harshal-thorat/",
    googleMaps: "https://maps.app.goo.gl/BWJViJMEGtDWmKoH9",
  },
  website: "thoratech.global",
};

export const NAV = [
  { label: "Home", href: "#hero" },
  { label: "Services", href: "#services" },
  { label: "How We Run It", href: "#how-we-run-it" },
  { label: "Why Us", href: "#why-us" },
  { label: "Success Story", href: "#success-story" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export const SERVICES = [
  {
    code: "01",
    title: "Sales & lead lifecycle",
    detail:
      "We govern the lead journey from first enquiry to closed order, so revenue does not leak between marketing, inside sales and dealers.",
    bullets: [
      "Lead qualification, scoring & Hot / Warm / Cold tagging",
      "IndiaMART & digital channel lead handling",
      "Dealer allocation & follow-up governance",
      "Opportunity aging control & win / loss tracking",
      "Inside sales support & revenue protection",
    ],
  },
  {
    code: "02",
    title: "Service & complaint governance",
    detail:
      "A structured complaint lifecycle that customers can feel: every complaint registered, routed, resolved, verified and learned from.",
    bullets: [
      "Complaint registration, routing & tracking",
      "MTTR monitoring & escalation control",
      "Closure verification with the customer",
      "NPS & post-resolution feedback capture",
      "Repeat-complaint identification & root-cause analysis",
    ],
  },
  {
    code: "03",
    title: "AMC & customer lifecycle",
    detail:
      "We protect installed-base revenue and customer data quality across the entire ownership period, not just the sale.",
    bullets: [
      "AMC renewal tracking & proactive outreach",
      "Extended-warranty selling",
      "Customer data verification & onboarding",
      "Database cleansing & master-data management",
      "Product registration & asset tracking",
    ],
  },
  {
    code: "04",
    title: "Dealer & channel governance",
    detail:
      "A central operating layer that keeps your dealer network onboarded, scored and engaged, so partner performance is managed, not assumed.",
    bullets: [
      "New dealer appointment support",
      "Dealer onboarding & welcome calls",
      "Performance monitoring & scorecards",
      "Milestone feedback at first order & install",
      "Disengagement & portal-usage flags",
    ],
  },
];

export const INDUSTRIES = {
  primary: {
    label: "Core domain",
    title: "Industrial, Manufacturing & Equipment",
    detail:
      "Our specialism. We run customer operations for industrial and manufacturing companies, equipment OEMs and the dealer networks that sell and service them.",
    tags: [
      "Gensets & power equipment",
      "Pumps & compressors",
      "Construction equipment",
      "Automotive & components",
      "Diesel-engine OEMs",
      "Equipment dealer networks",
    ],
  },
  growing: {
    label: "Growing capabilities",
    detail:
      "Sectors where we are extending the same operating discipline, with credible early experience.",
    items: [
      "Banking & Financial Services",
      "Consumer Products & Retail",
      "Field-Service Organisations",
      "B2B Sales Organisations",
    ],
  },
  disclaimer:
    "We headline only what we can prove today. A vertical is added as a focus area only after a real reference engagement in it.",
};

export const FOUNDER = {
  name: "Harshal Thorat",
  title: "Founder & CEO",
  bio: "Harshal has spent two decades running enterprise-grade customer operations, scaling one OEM engagement from 7 to 150+ FTEs over 12+ years and building and running a 700+ person operation across enterprise clients. Thoratech is built on that operating discipline: clear ownership, contractual SLAs and a governance rhythm you can audit at any time.",
  industriesServed: [
    "Diesel-engine OEMs",
    "Construction-equipment manufacturers",
    "Generator & power-equipment makers",
    "Automotive components",
    "NBFC & consumer lending",
    "B2B equipment dealer networks",
  ],
  industriesDisclaimer:
    "Founder's career history spans 15+ years across these industries. We anonymise specific brand engagements by design.",
};

export const HOW_WE_RUN_IT = [
  {
    label: "Single point of accountability",
    detail: "An onsite engagement lead owns your numbers and is your single point of contact.",
  },
  {
    label: "Dedicated remote operations team",
    detail: "A ring-fenced, trained team based in Pune. Your operation, our people.",
  },
  {
    label: "Tech enablement",
    detail: "Cloud dialer, CRM, Power BI dashboards, 100% call recording, dedicated QA.",
  },
  {
    label: "Governance rhythm",
    detail: "Daily MIS, weekly dashboards, monthly business reviews, contractual SLAs.",
  },
  {
    label: "Every channel your customers use",
    detail: "Phone, email, WhatsApp, chat, IVR and AI voice bot, unified under one operation.",
  },
];

export const WHY_US = [
  {
    title: "Outcomes, not seats",
    detail: "Engagements tied to results. We own the number, not the headcount.",
  },
  {
    title: "Governance built in",
    detail: "SLAs, dashboards and QA are wired in from day one. No retrofitting.",
  },
  {
    title: "One point of accountability",
    detail: "An engagement lead who owns your outcomes end-to-end.",
  },
  {
    title: "Industrial domain depth",
    detail: "Built around how manufacturers and dealer networks actually work.",
  },
  {
    title: "Founder-led",
    detail: "Run by an operator with two decades of managed-operations experience.",
  },
  {
    title: "AI-enabled, human-governed",
    detail:
      "Cloud dialer, transcription, sentiment analysis and AI call audit, run alongside a dedicated human QA layer.",
  },
];

export const SLA_TICKER = [
  { k: "Daily MIS", v: "DELIVERED" },
  { k: "Weekly dashboards", v: "REVIEWED" },
  { k: "Monthly business review", v: "GOVERNED" },
  { k: "Call recording", v: "100%" },
  { k: "Call transcription", v: "AUTOMATED" },
  { k: "AI call audit", v: "AI + MANUAL" },
  { k: "Sentiment analysis", v: "REAL-TIME" },
  { k: "AI voice bot", v: "ENABLED" },
  { k: "QA sampling", v: "DEDICATED" },
  { k: "Engagement lead", v: "ONSITE" },
  { k: "Channels", v: "OMNI" },
  { k: "SLA enforcement", v: "CONTRACTUAL" },
];

export const CHANNELS = [
  { icon: "phone", name: "Phone", detail: "Inbound & outbound voice with 100% call recording." },
  { icon: "mail", name: "Email", detail: "Templated responses with defined SLA turnaround." },
  { icon: "whatsapp", name: "WhatsApp", detail: "Business API with bot + live-agent handoff." },
  { icon: "chat", name: "Chat", detail: "Website & app chat with bot-to-human handoff." },
  { icon: "video", name: "Video", detail: "Customer demos and technical support sessions." },
  { icon: "ivr", name: "IVR", detail: "Automated routing and self-service menus." },
  { icon: "social", name: "Social media", detail: "Brand interaction and complaint capture." },
  { icon: "bot", name: "AI voice bot", detail: "Routine queries, outbound calling and lead qualification." },
];

// Careers page — 5 approved openings + working profile. No salaries by design.
export const CAREERS = {
  intro: {
    eyebrow: "Careers at Thoratech",
    title: "Build the operation",
    titleAccent: "that runs the operation.",
    body:
      "We are hiring operators, not seat-fillers. Every role at Thoratech sits inside a live governance rhythm of daily MIS, weekly QA and monthly reviews, and reports into an engagement lead who owns the number. If you have run a queue, owned an SLA or turned a chaotic process into a governed one, we want to talk.",
  },
  workplace: {
    location: "Balewadi, Pune",
    workingDays: "6 Days a week",
    setup: "Onsite operations centre",
    hours: "General shift + rotational for voice roles",
  },
  principles: [
    { n: "01", t: "Outcomes over hours", d: "We measure people on results and ownership, not seat-time." },
    { n: "02", t: "Governance from day one", d: "Daily MIS, weekly QA and monthly reviews, and you sit in them." },
    { n: "03", t: "Founder-led operating discipline", d: "Two decades of managed-operations experience, applied at your desk." },
  ],
  openings: [
    {
      id: "customer-care-executive",
      title: "Customer Care Executive",
      type: "Full-time · Onsite",
      summary:
        "Own the frontline conversation across voice, email, WhatsApp and chat. Register, route and resolve every enquiry inside SLA and keep the customer informed at every step.",
      responsibilities: [
        "Handle inbound & outbound customer interactions across channels",
        "Register complaints, route to the right team and track to closure",
        "Verify closure with the customer and capture post-resolution feedback",
        "Maintain accurate records in the CRM and daily MIS",
      ],
      musthaves: [
        "0–3 years in customer service, BPO or contact-centre operations",
        "Clear, professional communication in English + Hindi (Marathi a plus)",
        "Comfort with CRM, email and WhatsApp Business tools",
      ],
    },
    {
      id: "team-leader-operations",
      title: "Team Leader – Operations",
      type: "Full-time · Onsite",
      summary:
        "Run a small pod of executives to hit contractual SLAs. Own the daily huddle, weekly dashboards and QA calibration for your queue.",
      responsibilities: [
        "Lead a team of 6–12 executives and own daily/weekly SLA numbers",
        "Run daily huddles, weekly reviews and monthly business reviews",
        "Drive QA calibration, coaching and improvement plans",
        "Escalation management with the client engagement lead",
      ],
      musthaves: [
        "3+ years in BPO / customer operations with 1+ year leading a team",
        "Strong grasp of MIS, QA sampling and SLA governance",
        "Ability to coach, calibrate and hold a team to standard",
      ],
    },
    {
      id: "quality-training-executive",
      title: "Quality & Training Executive",
      type: "Full-time · Onsite",
      summary:
        "Own the quality bar and the ramp curve. Sample interactions, run calibration sessions and design training that actually moves the QA score.",
      responsibilities: [
        "Sample & score voice, email and chat interactions to QA framework",
        "Run weekly calibration with team leaders and the client",
        "Design and deliver induction + refresher training programmes",
        "Publish weekly QA dashboards and coaching notes",
      ],
      musthaves: [
        "2+ years in a QA / training role in a BPO or contact centre",
        "Working knowledge of QA sampling, TNI/TNA and calibration",
        "Excellent written communication for playbooks and training decks",
      ],
    },
    {
      id: "business-development-executive",
      title: "Business Development Executive",
      type: "Full-time · Onsite",
      summary:
        "Open new enterprise conversations for Thoratech. Research, qualify, and book audits with operations & CX leaders at manufacturers, dealers and B2B brands.",
      responsibilities: [
        "Build account lists and personalised outbound sequences",
        "Qualify prospects and book discovery calls / free audits",
        "Own the CRM hygiene for your pipeline, weekly forecast reviews",
        "Coordinate with the founder for proposals and pricing",
      ],
      musthaves: [
        "1–4 years in B2B inside sales or business development",
        "Confidence talking to Ops / Customer Service / Service heads",
        "Strong written English; comfortable with LinkedIn + email tooling",
      ],
    },
    {
      id: "hr-admin-executive",
      title: "HR & Admin Executive",
      type: "Full-time · Onsite",
      summary:
        "Keep the Thoratech operations centre humming. Own hiring pipelines, onboarding, statutory basics and day-to-day admin so operations never lose a minute.",
      responsibilities: [
        "Run end-to-end hiring for operations roles, from sourcing to onboarding",
        "Coordinate documentation, payroll inputs and statutory compliances",
        "Own the office administration and vendor management",
        "Support engagement, attendance discipline and employee wellbeing",
      ],
      musthaves: [
        "1–4 years in HR generalist or HR-admin roles (BPO preferred)",
        "Working knowledge of statutory basics (PF/ESIC/labour)",
        "Warm, organised, and comfortable talking to candidates all day",
      ],
    },
  ],
};
